import { Markdown } from "@/components/molecules/markdown";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  CardTitle as DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import {
  useAddMessageEntry,
  useCallbackSlot,
  useEditMessageEntry,
  useMessageEntries,
} from "@/features/messages/hooks/use-messages";
import type {
  ClientMessage,
  MessageEntry,
  MessageVisibility,
} from "@/features/messages/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  ArrowCounterClockwiseIcon,
  CaretDownIcon,
  CheckCircleIcon,
  DotsThreeIcon,
  TrashIcon,
  EyeIcon,
  LockSimpleIcon,
  PaperPlaneTiltIcon,
  RobotIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

/**
 * The discussion about one request, oldest first, and where to join it.
 *
 * A comment is **public** or **private**, chosen each time it is written: public is
 * for the customer — the AI tells them on their next call, whether or not the request
 * is closed — private stays with the team and never reaches the AI. Lines written by
 * the AI after a new call, and by the system (closed, reopened), are the team's.
 *
 * Writing a comment closes nothing. It schedules nothing either, unless a public
 * one is sent with "call the customer": the AI then calls to give that update, the
 * request staying open. The closing call back is chosen apart (`HandleMessageMenu`).
 */
export function MessageTimeline({
  message,
  canManage,
}: {
  message: ClientMessage;
  canManage: boolean;
}) {
  const { t } = useTranslation("messages");
  const { data, isLoading } = useMessageEntries(message.id);
  const entries = data?.entries || [];

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground">
        {t("timeline.title")}
      </h3>

      {isLoading ? null : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("timeline.empty")}</p>
      ) : (
        <ol className="space-y-3">
          {entries.map((entry) => (
            <TimelineEntry
              key={entry.id}
              entry={entry}
              messageId={message.id}
              canManage={canManage}
            />
          ))}
        </ol>
      )}

      {canManage && (
        // A fresh composer per request: what was typed for one never lands on the next.
        <Composer key={message.id} message={message} entries={entries} />
      )}
    </section>
  );
}

function TimelineEntry({
  entry,
  messageId,
  canManage,
}: {
  entry: MessageEntry;
  messageId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation("messages");
  const { format } = useDateFormat();
  const { company: clientId } = useParams<{ company: string }>();
  const when = format(entry.created_at, "Pp");

  if (entry.kind === "event") {
    const [code, detail = ""] = entry.event.split(":");
    const text =
      code === "reopened"
        ? t("timeline.reopened")
        : code === "visibility"
          ? t(`timeline.madeVisibility.${detail === "public" ? "public" : "private"}`, {
              excerpt: entry.body,
            })
          : t("timeline.closed", {
              resolution: t(`status.${detail || "handled_by_human"}`),
            });
    return (
      <li className="flex items-center gap-2 text-xs text-muted-foreground">
        {code === "reopened" ? (
          <ArrowCounterClockwiseIcon className="size-3.5 shrink-0" />
        ) : code === "visibility" ? (
          <EyeIcon className="size-3.5 shrink-0" />
        ) : (
          <CheckCircleIcon className="size-3.5 shrink-0" />
        )}
        <span>
          {text}
          {entry.author_name && ` · ${entry.author_name}`}
          {` · ${when}`}
        </span>
      </li>
    );
  }

  if (entry.kind === "update") {
    return (
      <li className="rounded-md border border-dashed bg-muted/30 p-3 text-sm">
        <div className="mb-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <RobotIcon className="size-3.5" />
          <span className="font-medium text-foreground">
            {t("timeline.agent")}
          </span>
          <span>{when}</span>
          {entry.session_id && (
            <Link
              to={`/${clientId}/calls/session/${entry.session_id}`}
              className="text-primary underline-offset-4 hover:underline"
            >
              {t("timeline.openCall")}
            </Link>
          )}
        </div>
        <p className="whitespace-pre-line">{entry.body}</p>
      </li>
    );
  }

  // Deleted: for a human colleague, never for the customer or the AI. The text is
  // gone from the server, this is all that is left of it.
  if (entry.removed_at) {
    return (
      <li className="flex items-center gap-2 rounded-md border border-dashed p-2 text-xs text-muted-foreground italic">
        <TrashIcon className="size-3.5 shrink-0" />
        {t("timeline.removed", {
          name: entry.removed_by_name || t("timeline.teamMember"),
          date: format(entry.removed_at, "Pp"),
        })}
      </li>
    );
  }

  const isPublic = entry.visibility === "public";
  return (
    <li
      className={cn(
        "rounded-md border p-3 text-sm",
        !isPublic && "border-amber-500/40 bg-amber-500/5",
      )}
    >
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {entry.author_name || t("timeline.teamMember")}
        </span>
        <span>{when}</span>
        <span
          className={cn(
            "flex items-center gap-1",
            !isPublic && "text-amber-600 dark:text-amber-400",
          )}
        >
          {isPublic ? (
            <EyeIcon className="size-3.5" />
          ) : (
            <LockSimpleIcon className="size-3.5" />
          )}
          {isPublic ? t("timeline.public") : t("timeline.private")}
        </span>
        {canManage && (
          <EntryMenu entry={entry} messageId={messageId} />
        )}
      </div>
      <Markdown>{entry.body}</Markdown>
      {isPublic && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {entry.delivered_at
            ? t("timeline.delivered", { date: format(entry.delivered_at, "Pp") })
            : t("timeline.notDelivered")}
        </p>
      )}
    </li>
  );
}

/**
 * The box to write in. Public or private is a choice made on every comment and
 * shown on the button, never a setting that persists: the one mistake worth
 * preventing is a team note the customer hears.
 *
 * The AI's draft answer (never validated) opens the public box while it holds
 * nothing of its own, labelled, and goes out only when somebody sends it.
 */
function Composer({
  message,
  entries,
}: {
  message: ClientMessage;
  entries: MessageEntry[];
}) {
  const { t } = useTranslation("messages");
  const add = useAddMessageEntry();
  const [visibility, setVisibility] = useState<MessageVisibility>("private");
  const [body, setBody] = useState("");

  const draft =
    !entries.some((entry) => entry.kind === "comment" && entry.visibility === "public") &&
    !message.reply
      ? message.suggested_reply
      : "";

  // The draft only ever fills an empty public box.
  useEffect(() => {
    if (draft && visibility === "public") setBody((current) => current || draft);
  }, [draft, visibility]);

  const text = body.trim();
  const usingDraft = !!draft && visibility === "public" && body === draft;

  // A public note on a request still open can also be told to the customer now,
  // by a call from the AI — the request stays on the list.
  const { format } = useDateFormat();
  const { data: slot } = useCallbackSlot(message.id, message.status === "pending");
  const canCall =
    message.status === "pending" &&
    visibility === "public" &&
    !!message.phone_number &&
    slot?.enabled !== false;

  const send = async (callback?: "now" | "later") => {
    try {
      await add.mutateAsync({ id: message.id, body: text, visibility, callback });
      setBody("");
      toast.success(
        callback
          ? t("timeline.sentAndCalling")
          : visibility === "public"
            ? t("timeline.sentPublic")
            : t("timeline.sentPrivate"),
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("handle.failed"));
    }
  };

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div role="tablist" className="flex gap-1 text-xs">
        {(["private", "public"] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={visibility === value}
            onClick={() => setVisibility(value)}
            className={cn(
              "flex items-center gap-1 rounded-md px-2 py-1 font-medium",
              visibility === value
                ? value === "public"
                  ? "bg-primary/10 text-primary"
                  : "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {value === "public" ? (
              <EyeIcon className="size-3.5" />
            ) : (
              <LockSimpleIcon className="size-3.5" />
            )}
            {t(`timeline.${value}Tab`)}
          </button>
        ))}
      </div>

      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder={t(`timeline.${visibility}Placeholder`)}
        aria-label={t("timeline.write")}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {usingDraft && <SparkleIcon className="size-3.5" weight="fill" />}
          {usingDraft ? t("handle.suggestionHint") : t(`timeline.${visibility}Hint`)}
        </p>
        <div className="flex">
          <Button
            type="button"
            size="sm"
            onClick={() => send()}
            disabled={!text || add.isPending}
            className={canCall ? "rounded-r-none" : undefined}
          >
            <PaperPlaneTiltIcon className="mr-1.5 size-4" />
            {visibility === "public" ? t("timeline.sendPublic") : t("timeline.sendPrivate")}
          </Button>
          {canCall && (
            // Not modal, as the closing menu.
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  className="rounded-l-none border-l border-primary-foreground/30 px-2"
                  disabled={!text || add.isPending}
                  aria-label={t("timeline.moreSendOptions")}
                >
                  <CaretDownIcon className="size-4" weight="bold" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuItem onClick={() => send()}>
                  <div className="space-y-0.5">
                    <div>{t("timeline.sendPublic")}</div>
                    <div className="text-muted-foreground text-xs">
                      {t("timeline.sendNextCallHint")}
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => send("now")}>
                  <div>{t("timeline.updateNow")}</div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => send("later")}
                  disabled={!slot?.at}
                >
                  <div className="space-y-0.5">
                    <div>{t("timeline.updateLater")}</div>
                    {!!slot?.at && (
                      <div className="text-muted-foreground text-xs">
                        {t("handle.outcomes.plannedFor", {
                          date: format(slot.at, "Pp"),
                        })}
                      </div>
                    )}
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * What can be done to a comment once written: make it public or private, or delete
 * it. Deleting asks first — it cannot be undone, and the text is erased for good.
 * Making a line private after the AI told the customer cannot take it back; the
 * confirmation says so.
 */
function EntryMenu({
  entry,
  messageId,
}: {
  entry: MessageEntry;
  messageId: string;
}) {
  const { t } = useTranslation("messages");
  const edit = useEditMessageEntry();
  const [confirming, setConfirming] = useState(false);
  const toPublic = entry.visibility === "private";

  const run = async (
    change: { visibility: MessageVisibility } | { remove: true },
  ) => {
    try {
      await edit.mutateAsync({ id: messageId, entryId: entry.id, ...change });
      toast.success(
        "remove" in change ? t("timeline.removedToast") : t("timeline.visibilityToast"),
      );
      setConfirming(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("handle.failed"));
    }
  };

  return (
    <>
      {/* Not modal, as in the closing menu: an item here opens a dialog. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto size-6"
            aria-label={t("timeline.actions")}
            disabled={edit.isPending}
          >
            <DotsThreeIcon className="size-4" weight="bold" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => run({ visibility: toPublic ? "public" : "private" })}
          >
            {toPublic ? (
              <EyeIcon className="mr-2 size-4" />
            ) : (
              <LockSimpleIcon className="mr-2 size-4" />
            )}
            {toPublic ? t("timeline.makePublic") : t("timeline.makePrivate")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => setConfirming(true)}
          >
            <TrashIcon className="mr-2 size-4" />
            {t("timeline.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("timeline.deleteTitle")}</DialogTitle>
            <DialogDescription>
              {entry.visibility === "public" && entry.delivered_at
                ? t("timeline.deleteDelivered")
                : t("timeline.deleteBody")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)}>
              {t("timeline.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={edit.isPending}
              onClick={() => run({ remove: true })}
            >
              {t("timeline.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
